import { makeRestApiRequest, RestApiRequestConfig } from '../../ts/services/RestApiService';
import { FetchDraftsRequestedAction, FetchDraftByParentIdRequestedAction } from '../actions/DraftsActions';
import { GetDraftsRequestBody, Draft} from '../types/DraftsTypes';
import { DiscardDraftRequestedAction } from '../actions/DiscardDraftAction';
import { Method } from 'axios';
import { L10n } from '../../ts/L10n/L10n';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { Messages } from '../../codegen/Localize';
import { WorkbookDraftDraftRestApiVersion } from './DraftsRestVersion';
import { FeatureFlags } from '../../ts/FeatureFlags';

export const DraftsRequests = {
  getDraftsRequest,
  getDraftByParentIdRequest,
  discardDraftRequest,
  callViaApiService,
};

type DraftRequestConfigType = Omit<RestApiRequestConfig<GetDraftsRequestBody>, 'requestBody' | 'headers' | 'queryParams'>;

export function getRequestConfig(httpMethod: Method, endpoint?: string): DraftRequestConfigType {
  return {
    restApiVersion: WorkbookDraftDraftRestApiVersion,
    httpMethod,
    endpoint: endpoint ? `workbookdrafts/${endpoint}`: 'workbookdrafts'
  };
}

export interface GetDraftsRequestParams {
  projectLuid: string;
}

export interface GetDraftsByIdRequestParams {
  parentRevisionId: string;
}

// Even though this looks\is the same as the one above, I am leaving them separate
// since one is the parameter for the request and the other one passed to the rest api for the query params
// and they can evolve separately. Also code will be more readable.
export interface GetDraftsRequestQueryParams {
  projectLuid: string;
}

export type QueryParams = GetDraftsRequestQueryParams | GetDraftsByIdRequestParams;

export interface DiscardDraftRequestParams {
  draftLuid: string;
}

export async function getDraftByParentIdRequest(action: FetchDraftByParentIdRequestedAction): Promise<Draft> {
  const { parentRevisionId } = action.data;
  const parentLuid = action.parentLuid;

  const config = getRequestConfig('GET', `findDraftByParentContent/${parentLuid}`);
  const queryParams = parentRevisionId ? {parentRevisionId} : undefined;
  const emptyBlob = getDraftsRequestBody();

  const draft = DraftsRequests.callViaApiService<Draft>(emptyBlob, config, queryParams);

  // Display of data is disabled by enableShowWorkbookAutoSaveOnVPC FF to enable shadow
  // deployment. This call would simply simulate loads on CDMS.
  return FeatureFlags.enableShowWorkbookAutoSaveOnVPC ? draft : null;
}

export async function getDraftsRequest(action: FetchDraftsRequestedAction): Promise<Draft[]> {
  const { projectLuid } = action.data;

  const requestQueryParams = getDraftsRequestQueryParam(projectLuid);
  const emptyBlob = getDraftsRequestBody();
  const config = getRequestConfig('GET');

  const drafts = DraftsRequests.callViaApiService<Draft[]>(emptyBlob, config, requestQueryParams);

  // Display of data is disabled by enableShowWorkbookAutoSaveOnVPC FF to enable shadow
  // deployment. This call would simply simulate loads on CDMS.
  return FeatureFlags.enableShowWorkbookAutoSaveOnVPC ? drafts : [];
}

export function discardDraftRequest(action: DiscardDraftRequestedAction): Promise<{}> {
  let dialogBody: string = Messages.confirmDeleteDraftWarning({
    DRAFT_CONTENT_TYPE: action.contentType,
    DRAFT_NAME: L10n.elideIfLong(action.name)
  });

  function onClickDiscard(draftLuid: string): PromiseLike<{}> {
    const config = getRequestConfig('DELETE', draftLuid);
    const emptyBlob = getDraftsRequestBody();

    return DraftsRequests.callViaApiService(emptyBlob, config);
  }

  const initialModalInstance = openReactConfirmationDialog<string, {}>({
    title: Messages.deleteMessage(),
    message: dialogBody,
    paramsForAction: action.draftLuid,
    actionName: Messages.deleteDraft(),
    action: onClickDiscard,
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  return initialModalInstance.result;
}

export function callViaApiService<ReturnType>(
  body: GetDraftsRequestBody,
  requestConfig: DraftRequestConfigType,
  queryParams?: QueryParams,
  headers?: {[index: string]: string},
) {
  const config: RestApiRequestConfig<GetDraftsRequestBody, QueryParams> = {
    ...requestConfig,
    requestBody: body,
    headers,
    queryParams,
  };

  return makeRestApiRequest<Blob, ReturnType>(config);
}

function getDraftsRequestQueryParam(projectLuid: string): GetDraftsRequestQueryParams {
  //api/${WorkbookDraftDraftRestApiVersion}/siteluid/workbookdrafts?projectluid=something
  return projectLuid? {projectLuid} : undefined;
}

function getDraftsRequestBody(): Blob {
  //TODO I can probably get away without this. Confirm once I can run against the product
  return new Blob([]);
}
